import { datadogRum } from '@datadog/browser-rum';

import type {
  InviteEligibilityFinalOutcome,
  InviteEligibilityCheckSummary,
  InviteEligibilityFlow,
  InviteEligibilityPreflightOutcome,
  InviteEligibilityRowProperties,
  InviteSubmitErrorType,
} from './utils/invite-eligibility-telemetry.util';

export const INVITE_ELIGIBILITY_RUM_ACTIONS = {
  CheckCompleted: 'Invite Eligibility Check Completed',
  WarningShown: 'Invite Eligibility Warning Shown',
  BlockShown: 'Invite Eligibility Block Shown',
  RecordLinkClicked: 'Invite Eligibility Record Link Clicked',
  SubmitCompleted: 'Invite Eligibility Submit Completed',
} as const;

const buildContext = (flow: InviteEligibilityFlow) => ({
  feature: 'invite_eligibility',
  invite_flow: flow,
});

const durationMs = (value: number) => Math.round(value);

export const recordInviteEligibilityCheckCompleted = (
  flow: InviteEligibilityFlow,
  summary: InviteEligibilityCheckSummary,
  elapsedMs: number,
) => {
  datadogRum.addAction(INVITE_ELIGIBILITY_RUM_ACTIONS.CheckCompleted, {
    ...buildContext(flow),
    decision: summary.outcome,
    reason: summary.reason,
    applicant_count: summary.applicant_count,
    match_count: summary.match_count,
    source_systems: summary.source_systems,
    duration_ms: durationMs(elapsedMs),
  });
};

/** The check failed open: the form stays usable, no banner shows and Send stays enabled. */
export const recordInviteEligibilityCheckUnavailable = (
  flow: InviteEligibilityFlow,
  elapsedMs: number,
  httpStatus?: number,
) => {
  const context = {
    ...buildContext(flow),
    decision: 'unavailable',
    duration_ms: durationMs(elapsedMs),
    ...(httpStatus ? { http_status: httpStatus } : {}),
  };
  datadogRum.addAction(INVITE_ELIGIBILITY_RUM_ACTIONS.CheckCompleted, context);
  if (httpStatus !== undefined && httpStatus >= 500) {
    datadogRum.addError(new Error('Invite eligibility check unavailable'), context);
  }
};

export const recordInviteEligibilityBannerShown = (
  flow: InviteEligibilityFlow,
  outcome: 'warning' | 'block',
  row: InviteEligibilityRowProperties,
) => {
  datadogRum.addAction(
    outcome === 'warning'
      ? INVITE_ELIGIBILITY_RUM_ACTIONS.WarningShown
      : INVITE_ELIGIBILITY_RUM_ACTIONS.BlockShown,
    { ...buildContext(flow), ...row },
  );
};

export const recordInviteEligibilityRecordLinkClicked = (
  flow: InviteEligibilityFlow,
  outcome: string,
  matchCount: number,
) => {
  datadogRum.addAction(INVITE_ELIGIBILITY_RUM_ACTIONS.RecordLinkClicked, {
    ...buildContext(flow),
    outcome,
    match_count: matchCount,
  });
};

export const recordInviteEligibilitySubmitCompleted = (
  flow: InviteEligibilityFlow,
  submit: {
    preflightOutcome: InviteEligibilityPreflightOutcome;
    finalOutcome: InviteEligibilityFinalOutcome;
    succeeded: boolean;
    errorType?: InviteSubmitErrorType;
  },
) => {
  datadogRum.addAction(INVITE_ELIGIBILITY_RUM_ACTIONS.SubmitCompleted, {
    ...buildContext(flow),
    preflight_outcome: submit.preflightOutcome,
    final_outcome: submit.finalOutcome,
    result: submit.succeeded ? 'success' : 'failure',
    ...(submit.errorType ? { error_type: submit.errorType } : {}),
  });
};
